import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Pagination} from 'lib/Listings';
import {GLOBAL_SEARCH_URL} from 'search/SearchConstants';
import * as net from 'util/Net';

import {useVisibilityAwarePolling} from '../lib/useVisibilityAwarePolling';
import type {SortConfig} from '../runs/PredictionBatchTypes';
import {STATUS_CODE_TO_STATUS} from '../runs/PredictionBatchTypes';
import type {PredictionJobRow} from './PredictionJobTypes';

export interface PredictionJobSearchFilters {
  query: string;
  predictionBatchId: string | null;
  modelName: string | null;
  // Raw indexed prediction job status values. Omit / null / [] => all statuses.
  statuses?: ReadonlyArray<string> | null;
}

// Convenience for callers that want completed-only results (e.g. the standalone Predictions page).
export const COMPLETED_ONLY: ReadonlyArray<string> = ['COMPLETED'];

export const DEFAULT_FILTERS: PredictionJobSearchFilters = {
  query: '',
  predictionBatchId: null,
  modelName: null,
};

interface PredictionJobListingData {
  id: string;
  name: string;
  predictionBatchId: string;
  predictionBatch?: {id: string; name: string} | null;
  modelName: string;
  componentApiIds: Array<string>;
  configJson?: Record<string, unknown> | null;
  status: string;
  hasResultsFile: boolean;
  standardizedOutputDownloadUrl: string | null;
  createdAt: string;
  modifiedAt: string;
}

interface SearchResponse {
  ids: Array<string>;
  total: number;
  isExact: boolean;
  predictionJobsById?: Record<string, PredictionJobListingData>;
}

function buildSearchFilters(filters: PredictionJobSearchFilters): Array<Record<string, unknown>> {
  const searchFilters: Array<Record<string, unknown>> = [
    {key: 'TYPES', operator: 'IS_ONE_OF', value: [ModelTypeConstants.PREDICTION_JOB]},
  ];

  // Only constrain by status when the caller asks for it; omitting statuses returns all statuses.
  if (filters.statuses && filters.statuses.length > 0) {
    searchFilters.push({
      key: 'PREDICTION_JOB_STATUS',
      operator: 'IS_ONE_OF',
      value: [...filters.statuses],
    });
  }

  if (filters.predictionBatchId) {
    searchFilters.push({
      key: 'prediction_job_batch_api_id',
      operator: 'IS_ONE_OF',
      value: [filters.predictionBatchId],
    });
  }

  if (filters.modelName) {
    searchFilters.push({
      key: 'PREDICTION_JOB_MODEL_NAME',
      operator: 'IS_ONE_OF',
      value: [filters.modelName],
    });
  }

  return searchFilters;
}

function buildSort(sort: SortConfig): Array<{key: string; reverse: boolean}> {
  const sortKeyMap: Record<string, string> = {
    createdAt: 'created_at',
    modifiedAt: 'modified_at',
    name: 'name',
  };
  return [{key: sortKeyMap[sort.key], reverse: sort.direction === 'desc'}];
}

function mapListingToRow(id: string, listing: PredictionJobListingData | undefined): PredictionJobRow {
  if (!listing) {
    return {
      id,
      name: '',
      predictionBatchId: '',
      predictionBatchName: '',
      modelName: '',
      componentApiIds: [],
      configJson: {},
      status: 'PENDING',
      hasResultsFile: false,
      standardizedOutputDownloadUrl: null,
      createdAt: '',
      modifiedAt: '',
    };
  }
  return {
    id: listing.id,
    name: listing.name,
    predictionBatchId: listing.predictionBatchId ?? '',
    predictionBatchName: listing.predictionBatch?.name ?? '',
    modelName: listing.modelName ?? '',
    componentApiIds: listing.componentApiIds ?? [],
    configJson: listing.configJson ?? {},
    status: STATUS_CODE_TO_STATUS[listing.status] ?? 'PENDING',
    hasResultsFile: listing.hasResultsFile ?? false,
    standardizedOutputDownloadUrl: listing.standardizedOutputDownloadUrl ?? null,
    createdAt: listing.createdAt ?? '',
    modifiedAt: listing.modifiedAt ?? '',
  };
}

interface UsePredictionJobSearchOptions {
  /** Refetch on this interval while the tab is visible. Omit to fetch only on demand. */
  pollIntervalMs?: number;
}

interface UsePredictionJobSearchResult {
  jobs: Array<PredictionJobRow>;
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePredictionJobSearch(
  filters: PredictionJobSearchFilters,
  sort: SortConfig,
  offset: number,
  limit: number,
  {pollIntervalMs}: UsePredictionJobSearchOptions = {}
): UsePredictionJobSearchResult {
  const [jobs, setJobs] = React.useState<Array<PredictionJobRow>>([]);
  const [pagination, setPagination] = React.useState<Pagination>({offset: 0, limit, total: 0});
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = React.useState(0);
  const isFetchingRef = React.useRef(false);
  const isBackgroundRefreshRef = React.useRef(false);

  const refresh = React.useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  // A tick is skipped while a request is outstanding so a poll never supersedes a user-initiated load.
  const poll = React.useCallback(() => {
    if (isFetchingRef.current) {
      return;
    }
    isBackgroundRefreshRef.current = true;
    setRefreshCounter((prev) => prev + 1);
  }, []);
  useVisibilityAwarePolling(pollIntervalMs != null, pollIntervalMs ?? 0, poll);

  React.useEffect(() => {
    let cancelled = false;
    // Background refreshes keep the current rows, or error, on screen until a response replaces them.
    const isBackgroundRefresh = isBackgroundRefreshRef.current;
    isBackgroundRefreshRef.current = false;
    isFetchingRef.current = true;
    if (!isBackgroundRefresh) {
      setIsLoading(true);
      setError(null);
    }

    const searchParams = {
      offset,
      limit,
      query: filters.query,
      filters: buildSearchFilters(filters),
      sorts: buildSort(sort),
    };

    net
      .postJSONPromise({url: GLOBAL_SEARCH_URL, data: searchParams})
      .then((response: SearchResponse) => {
        if (cancelled) {
          return;
        }
        isFetchingRef.current = false;
        const byId = response.predictionJobsById ?? {};
        const rows = response.ids.map((id) => mapListingToRow(id, byId[id]));
        setJobs(rows);
        setPagination({
          offset,
          limit,
          total: response.total,
          isExact: response.isExact,
        });
        setError(null);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        isFetchingRef.current = false;
        // A transient poll failure leaves the page as it was; the next poll retries.
        if (isBackgroundRefresh) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Search failed');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, sort, offset, limit, refreshCounter]);

  return {jobs, pagination, isLoading, error, refresh};
}
